import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, IconButton, Paper,
  TextField, InputAdornment, List, ListItem, ListItemText, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Repeat as RepeatIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
  Map as MapIcon,
  Assignment as LineupIcon,
} from '@mui/icons-material';
import { EVENT_TYPES, RECURRING_INTERVALS } from '../constants';
import { hasLineupAssignments } from '../utils/lineup';

export default function EventDetailDialog({
  event, open, onClose, onSignup, onDecline, players, currentUserEmail, currentUserRole,
  onPlayerClick, onEdit, onDelete, onSignupFor, onDeclineFor, onOpenLineup,
}) {
  const [playerSearch, setPlayerSearch] = useState('');
  const [showMap, setShowMap] = useState(false);
  if (!event) return null;
  const findPlayer = (key) => key && key.startsWith('player-')
    ? (players || []).find(p => p.id === parseInt(key.slice(7)))
    : (players || []).find(p => p.email === key);
  const meta = EVENT_TYPES.find(t => t.value === event.type) || EVENT_TYPES[0];
  const occurrenceDate = event.recurring ? (event.occurrenceDate || event.date) : null;
  const displaySignups = occurrenceDate
    ? (event.signups || event.occurrenceSignups?.[occurrenceDate] || [])
    : (event.signups || []);
  const displayDeclines = occurrenceDate
    ? (event.declines || event.occurrenceDeclines?.[occurrenceDate] || [])
    : (event.declines || []);
  const intervalLabel = RECURRING_INTERVALS.find(r => r.value === event.recurringInterval)?.label || 'Wöchentlich';
  const recurringLabel = event.recurringDays?.length
    ? `${intervalLabel} · ${event.recurringDays.join(', ')}`
    : intervalLabel;
  const invitedPlayers = (players || []).filter(p => event.invitedPlayerIds?.includes(p.id));
  const normalizeAttendanceToken = (token) => {
    if (!token) return '';
    const trimmed = String(token).trim();
    if (trimmed.startsWith('player-')) return trimmed;
    const matchedPlayer = (players || []).find(p => p.email && p.email === trimmed);
    return matchedPlayer ? `player-${matchedPlayer.id}` : trimmed;
  };
  const normalizeAttendanceList = (list) => {
    const seen = new Set();
    const normalized = [];
    (Array.isArray(list) ? list : []).forEach(entry => {
      const token = normalizeAttendanceToken(entry);
      if (!token || seen.has(token)) return;
      seen.add(token);
      normalized.push(token);
    });
    return normalized;
  };
  const signedUpKeys = normalizeAttendanceList(displaySignups);
  const declinedKeys = normalizeAttendanceList(displayDeclines);
  const currentUserAttendanceKey = normalizeAttendanceToken(currentUserEmail);
  const hasSignedUp = signedUpKeys.includes(currentUserAttendanceKey);
  const hasDeclined = declinedKeys.includes(currentUserAttendanceKey);
  const playerRoster = (players || [])
    .filter(p => event.invitedPlayerIds?.includes(p.id))
    .slice()
    .sort((a, b) => {
      const statusOrder = (p) => {
        const pid = `player-${p.id}`;
        if (displaySignups.includes(pid)) return 0;
        if (displayDeclines.includes(pid)) return 1;
        return 2;
      };
      return statusOrder(a) - statusOrder(b) || `${a.lastName || ''}`.localeCompare(b.lastName || '', 'de') || `${a.firstName || ''}`.localeCompare(b.firstName || '', 'de');
    });
  const isStaffView = currentUserRole === 'admin' || currentUserRole === 'trainer';
  const attendanceTooltipProps = {
    disableInteractive: true,
    enterDelay: 250,
    enterNextDelay: 250,
    leaveDelay: 100,
    placement: 'top',
  };
  const getPlayerStatus = (player) => {
    const identifiers = normalizeAttendanceList([
      `player-${player.id}`,
      player.email,
    ]);
    if (identifiers.some(id => signedUpKeys.includes(id))) return 'signed';
    if (identifiers.some(id => declinedKeys.includes(id))) return 'declined';
    return 'open';
  };
  const statusChip = (status) => status === 'signed' ? (
    <Chip size="small" label="zugesagt" sx={{ height: 18, fontSize: 10, bgcolor: '#E8F5E9', color: '#2E7D32' }} />
  ) : status === 'declined' ? (
    <Chip size="small" label="abgesagt" sx={{ height: 18, fontSize: 10, bgcolor: '#FFEBEE', color: '#C62828' }} />
  ) : (
    <Chip size="small" label="offen" sx={{ height: 18, fontSize: 10, bgcolor: '#ECEFF1', color: '#546E7A' }} />
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ color: meta.color }}>{meta.icon}</Box>
          <Typography fontWeight={700} fontSize={18}>{event.name}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1 } }}>
          <Chip icon={meta.icon} label={meta.label} size="small"
            sx={{ bgcolor: `${meta.color}18`, color: meta.color, fontWeight: 600, height: { xs: 24, sm: 26 } }} />
          <Chip icon={<CalendarIcon fontSize="small" />}
            label={event.endDate && event.endDate !== event.date ? `${event.date} – ${event.endDate}` : event.date}
            size="small" variant="outlined" sx={{ height: { xs: 24, sm: 26 } }} />
          {event.startTime && (
            <Chip
              label={event.endTime ? `${event.startTime} – ${event.endTime} Uhr` : `ab ${event.startTime} Uhr`}
              size="small" variant="outlined" sx={{ fontWeight: 600, height: { xs: 24, sm: 26 } }} />
          )}
          {event.recurring && (
            <Chip icon={<RepeatIcon fontSize="small" />} label={recurringLabel}
              size="small" variant="outlined" color="secondary" sx={{ height: { xs: 24, sm: 26 } }} />
          )}
        </Box>
        {event.location && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <LocationIcon fontSize="small" />
              <Typography variant="body2" sx={{ flex: 1 }}>{event.location}</Typography>
              <Button size="small" startIcon={<MapIcon fontSize="small" />} onClick={() => setShowMap(v => !v)}
                sx={{ color: '#0D47A1', textTransform: 'none', fontSize: 12, py: 0, minWidth: 0 }}>
                {showMap ? 'Karte schließen' : 'Karte'}
              </Button>
              <IconButton size="small" component="a"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                target="_blank" rel="noopener noreferrer"
                sx={{ color: '#0D47A1' }}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Box>
            {showMap && (
              <Box sx={{ mt: 1, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e7ef' }}>
                <iframe
                  title="Karte"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(event.location)}&output=embed`}
                  width="100%" height="220" style={{ border: 0, display: 'block' }}
                  loading="lazy"
                />
              </Box>
            )}
          </Box>
        )}
        {event.description && (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
            <Typography variant="body2" color="text.secondary">{event.description}</Typography>
          </Paper>
        )}
        {event.createdBy && (
          <Typography variant="caption" color="text.secondary">
            Erstellt von <strong>{event.createdBy}</strong>
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 3 }, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon fontSize="small" sx={{ color: '#2E7D32' }} />
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{signedUpKeys.length || 0} Zusagen</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CancelIcon fontSize="small" sx={{ color: '#C62828' }} />
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{declinedKeys.length || 0} Absagen</Typography>
            </Box>
          </Box>
          {onOpenLineup && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Button size="small" startIcon={<LineupIcon />} variant="outlined"
                onClick={onOpenLineup}
                sx={{ borderColor: '#0D47A1', color: '#0D47A1', textTransform: 'none', fontWeight: 600, borderRadius: 2,
                  minWidth: 0, px: { xs: 1, sm: 1.5 }, fontSize: { xs: 11, sm: 13 } }}>
                Aufstellung
              </Button>
              {event.lineupPublished && hasLineupAssignments(event.lineup) && (
                <Chip size="small" label="Veröffentlicht ✓"
                  sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 700, fontSize: 11 }} />
              )}
            </Box>
          )}
        </Box>
        {isStaffView ? (
          onSignupFor && playerRoster.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Eingeladene Spieler ({playerRoster.length})
              </Typography>
              <TextField size="small" fullWidth variant="outlined"
                placeholder="Spieler suchen…" value={playerSearch}
                onChange={e => setPlayerSearch(e.target.value)}
                sx={{ mt: 0.75 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
                  endAdornment: playerSearch ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setPlayerSearch('')}><CloseIcon fontSize="small" /></IconButton>
                    </InputAdornment>
                  ) : null,
                }} />
              <List dense disablePadding sx={{ mt: 0.5, maxHeight: 240, overflowY: 'auto', pr: 1.25 }}>
                {playerRoster.filter(p => {
                  if (!playerSearch) return true;
                  const q = playerSearch.toLowerCase();
                  return `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) || p.capNumber?.toString().includes(q);
                }).map(p => {
                  const pid = `player-${p.id}`;
                  const status = getPlayerStatus(p);
                  return (
                    <ListItem key={p.id} disablePadding sx={{ py: 0.25, borderRadius: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => onPlayerClick && onPlayerClick(p)}>
                            <Typography variant="body2" fontWeight={600}>{`${p.firstName} ${p.lastName}`}</Typography>
                            {statusChip(status)}
                          </Box>
                        }
                        />
                      <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
                        <Tooltip title={status === 'signed' ? 'Zugesagt' : 'Zusagen'} {...attendanceTooltipProps}>
                          <IconButton size="small"
                            onClick={() => onSignupFor(pid)}
                            sx={{ color: status === 'signed' ? '#2E7D32' : 'text.disabled',
                              bgcolor: status === 'signed' ? '#E8F5E9' : 'transparent', width: 28, height: 28 }}>
                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={status === 'declined' ? 'Abgesagt' : 'Absagen'} {...attendanceTooltipProps}>
                          <IconButton size="small"
                            onClick={() => onDeclineFor && onDeclineFor(pid)}
                            sx={{ color: status === 'declined' ? '#C62828' : 'text.disabled',
                              bgcolor: status === 'declined' ? '#FFEBEE' : 'transparent', width: 28, height: 28 }}>
                            <CancelIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )
        ) : invitedPlayers.length > 0 ? (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Anwesenheit</Typography>
            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {invitedPlayers.map(p => {
                const status = getPlayerStatus(p);
                const colorSx = status === 'signed'
                  ? { bgcolor: '#E8F5E9', color: '#2E7D32', borderColor: '#2E7D3233' }
                  : status === 'declined'
                    ? { bgcolor: '#FFEBEE', color: '#C62828', borderColor: '#C6282833' }
                    : { bgcolor: '#ECEFF1', color: '#546E7A', borderColor: '#546E7A33' };
                const statusLabel = status === 'signed' ? 'Zugesagt' : status === 'declined' ? 'Abgesagt' : 'Offen';
                return (
                  <Chip
                    key={p.id}
                    clickable={!!onPlayerClick}
                    onClick={() => onPlayerClick && onPlayerClick(p)}
                    label={`${p.firstName} ${p.lastName}`}
                    size="small"
                    title={statusLabel}
                    sx={{
                      height: 24,
                      fontSize: 12,
                      fontWeight: 600,
                      border: '1px solid',
                      ...colorSx,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        ) : (event.signups?.length > 0 || event.declines?.length > 0) && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Teilnehmer</Typography>
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {event.signups?.map(key => {
                const pl = findPlayer(key);
                return (
                  <ListItem key={key} disablePadding
                    sx={{ py: 0.25, borderRadius: 1, cursor: pl ? 'pointer' : 'default',
                      '&:hover': pl ? { bgcolor: '#f0f7ff' } : {} }}
                    onClick={() => pl && onPlayerClick && onPlayerClick(pl)}>
                    <CheckCircleIcon fontSize="small" sx={{ color: '#2E7D32', mr: 1, flexShrink: 0 }} />
                    <ListItemText
                      primary={pl ? `${pl.firstName} ${pl.lastName}` : key}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: pl ? 600 : 400 }} />
                  </ListItem>
                );
              })}
              {event.declines?.map(key => {
                const pl = findPlayer(key);
                return (
                  <ListItem key={key} disablePadding
                    sx={{ py: 0.25, borderRadius: 1, cursor: pl ? 'pointer' : 'default',
                      '&:hover': pl ? { bgcolor: '#f0f7ff' } : {} }}
                    onClick={() => pl && onPlayerClick && onPlayerClick(pl)}>
                    <CancelIcon fontSize="small" sx={{ color: '#C62828', mr: 1 }} />
                    <ListItemText
                      primary={pl ? `${pl.firstName} ${pl.lastName}` : key}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', fontWeight: pl ? 600 : 400 }} />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Termin als Text kopieren (z.B. für WhatsApp)">
          <Button size="small" startIcon={<ShareIcon />} color="inherit"
            onClick={() => {
              const typeMeta = EVENT_TYPES.find(t => t.value === event.type);
              const dateStr = event.endDate && event.endDate !== event.date
                ? `${event.date} \u2013 ${event.endDate}` : event.date;
              const timeStr = event.startTime
                ? (event.endTime ? ` \u00b7 ${event.startTime} \u2013 ${event.endTime} Uhr` : ` \u00b7 ab ${event.startTime} Uhr`)
                : '';
              const link = `${window.location.origin}/?event=${event.id}`;
              const text = [
                `\ud83d\udcc5 *${event.name}*`,
                `${typeMeta?.label || ''} \u00b7 ${dateStr}${timeStr}`,
                event.location ? `\ud83d\udccd ${event.location}` : '',
                event.description ? `\n${event.description}` : '',
                `\n\ud83d\udc49 Zu-/Absagen:\n${link}`,
              ].filter(Boolean).join('\n');
              navigator.clipboard.writeText(text).catch(() => {});
            }}>
            Teilen
          </Button>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {onEdit && <Tooltip title="Bearbeiten"><IconButton size="small" onClick={() => onEdit(event)} sx={{ color: '#0D47A1', mr: 0.5 }}><EditIcon /></IconButton></Tooltip>}
        {onDelete && <Tooltip title="Löschen"><IconButton size="small" onClick={() => onDelete(event)} sx={{ color: '#C62828', mr: 1 }}><DeleteIcon /></IconButton></Tooltip>}
        <Button onClick={onClose} color="inherit">Schließen</Button>
        <Button variant={hasDeclined ? 'outlined' : 'text'} color="error" startIcon={<CancelIcon />}
          onClick={() => onDecline(event.id, occurrenceDate || event.date)} disabled={hasDeclined}>
          {hasDeclined ? 'Abgesagt' : 'Absagen'}
        </Button>
        <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
          onClick={() => onSignup(event.id, occurrenceDate || event.date)} disabled={hasSignedUp}>
          {hasSignedUp ? 'Zugesagt ✓' : 'Zusagen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
